import { BillDeferredReason } from "@redotech/cockroachdb-codegen/tables/billing/bill-deferred-reason";
import { BillStatus } from "@redotech/cockroachdb-codegen/tables/billing/bill-status";
import { BillType } from "@redotech/cockroachdb-codegen/tables/billing/bill-type";
import {
  BillDisplayStatus,
  PaymentProvider,
} from "@redotech/redo-billing-common/billing-display-labels";
import { SubscriptionProduct } from "@redotech/redo-billing-common/unified-billing/enums";
import * as z from "zod";

export const billTypeSchema = z.enum(BillType);
export const billStatusSchema = z.enum(BillStatus);
export const billDisplayStatusSchema = z.enum(BillDisplayStatus);
export const billDeferredReasonSchema = z.enum(BillDeferredReason);
export const subscriptionProductSchema = z.enum(SubscriptionProduct);
export const paymentProviderSchema = z.enum(PaymentProvider);

const paymentMethodInfoSchema_ = z.object({
  provider: paymentProviderSchema.nullable(),
  last4: z.string().nullable(),
  brand: z.string().nullable(),
});
export const paymentMethodInfoSchema: z.ZodType<PaymentMethodInfo> =
  paymentMethodInfoSchema_;
export interface PaymentMethodInfo extends z.infer<
  typeof paymentMethodInfoSchema_
> {}

const billSchema_ = z.object({
  id: z.string(),
  type: billTypeSchema,
  status: billStatusSchema,
  displayStatus: billDisplayStatusSchema,
  startTime: z.date(),
  endTime: z.date(),
  /** Invoice-signed for every bill type: positive means the merchant owes. */
  balanceStart: z.number(),
  balanceEnd: z.number(),
  totalCharged: z.number(),
  unstruckForeign: z
    .array(z.object({ currency: z.string(), amount: z.number() }))
    .nullish(),
  totalPaid: z.number(),
  products: z.array(subscriptionProductSchema),
  hasBundleDiscount: z.boolean().default(false),
  createdAt: z.date(),
  // When the bill is due/charged — the "Billed" date. Forward subscriptions are
  // due on their start day, arrears/usage bills at period close. Null falls back
  // to end_time (see getBilledDate).
  billingDueDate: z.date().nullish(),
  /** Why collection was deferred; `payment_terms` marks a net terms invoice. */
  deferredReason: billDeferredReasonSchema.nullish(),
  currency: z.string().default("USD"),
  paymentMethod: paymentMethodInfoSchema.nullable(),
});
export const billSchema: z.ZodType<BillSchema> = billSchema_;
export interface BillSchema extends z.infer<typeof billSchema_> {}

const getBillsInput_ = z.object({
  pageSize: z.number().optional().default(25),
  offset: z.number().optional().default(0),
  sortKey: z
    .enum([
      "created_at",
      "start_time",
      "end_time",
      "billing_due_date",
      "status",
    ])
    .optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
  status: billStatusSchema.optional(),
  excludeStatus: billStatusSchema.optional(),
  types: z.array(billTypeSchema).optional(),
  // Server-side filters backing the unified-billing table (so 20-row server
  // pagination stays correct across the whole result set, not just one page).
  statuses: z.array(billStatusSchema).optional(),
  products: z.array(subscriptionProductSchema).optional(),
  // Billed-date filter (the table's "Bill date" column / filter, and the summary
  // month picker): matches on billing_due_date, falling back to end_time when
  // the bill has no due date.
  billedAfter: z.date().optional(),
  billedBefore: z.date().optional(),
  // Free-text: matched server-side against status and product labels.
  search: z.string().optional(),
});
export const getBillsInput: z.ZodType<GetBillsInput> = getBillsInput_;
export interface GetBillsInput extends z.infer<typeof getBillsInput_> {}

const getBillsOutput_ = z.object({
  bills: z.array(billSchema),
  total: z.number(),
  nextOffset: z.number().nullish(),
});
export const getBillsOutput: z.ZodType<GetBillsOutput> = getBillsOutput_;
export interface GetBillsOutput extends z.infer<typeof getBillsOutput_> {}

export const getBills = {
  name: "getBills" as const,
  input: getBillsInput,
  output: getBillsOutput,
};
